import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  TextField,
  Box,
  Typography,
  Chip,
  Stack,
  Divider,
  CircularProgress,
} from "@mui/material";
import WarehouseIcon from "@mui/icons-material/Warehouse";
import EngineeringIcon from "@mui/icons-material/Engineering";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import locationsService from "../../../api/locationsService";

const LocationTypeIcon = ({ type }) => {
  switch (type) {
    case "main_warehouse":
      return <WarehouseIcon color="primary" />;
    case "technician":
      return <EngineeringIcon color="secondary" />;
    case "dangerous_goods":
      return <DeleteForeverIcon color="error" />;
    default:
      return <WarehouseIcon />;
  }
};

const LocationTypeLabel = ({ type }) => {
  const labels = {
    main_warehouse: "Almacén Principal",
    technician: "Técnico",
    dangerous_goods: "Residuos Peligrosos",
  };
  return labels[type] || type;
};

export default function LocationsModal({ open, onClose, onSuccess }) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newLocationName, setNewLocationName] = useState("");
  const [newLocationType, setNewLocationType] = useState("main_warehouse");

  useEffect(() => {
    if (open) {
      loadLocations();
    }
  }, [open]);

  const loadLocations = async () => {
    try {
      setLoading(true);
      const response = await locationsService.getAllLocations();
      setLocations(response.data);
    } catch (error) {
      console.error("Error cargando ubicaciones:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newLocationName.trim()) {
      alert("Por favor ingresa un nombre para la ubicación");
      return;
    }

    try {
      setCreating(true);
      await locationsService.createLocation({
        type: newLocationType,
        name: newLocationName.trim(),
      });
      setNewLocationName("");
      setNewLocationType("main_warehouse");
      await loadLocations();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error creando ubicación:", error);
      alert(error.response?.data?.message || "Error al crear la ubicación");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de que deseas eliminar esta ubicación?")) {
      return;
    }

    try {
      await locationsService.deleteLocation(id);
      await loadLocations();
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Error eliminando ubicación:", error);
      alert(error.response?.data?.message || "Error al eliminar la ubicación");
    }
  };

  // Filtrar solo almacenes principales (no técnicos ni residuos)
  const mainWarehouses = locations.filter(
    (loc) => loc.type === "main_warehouse" && loc.status === "active"
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <WarehouseIcon color="primary" />
          <Typography variant="h6" fontWeight={700}>
            Gestionar Almacenes
          </Typography>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {/* Formulario para crear nuevo almacén */}
        <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            Crear nuevo almacén
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
            <TextField
              fullWidth
              size="small"
              label="Nombre del almacén"
              value={newLocationName}
              onChange={(e) => setNewLocationName(e.target.value)}
              placeholder="Ej: Almacén Secundario, Bodega Norte, etc."
              disabled={creating}
            />
            <Button
              variant="contained"
              startIcon={creating ? <CircularProgress size={20} color="inherit" /> : <AddIcon />}
              onClick={handleCreate}
              disabled={creating || !newLocationName.trim()}
              sx={{ minWidth: 120 }}
            >
              Crear
            </Button>
          </Stack>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Lista de almacenes existentes */}
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          Almacenes existentes
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : mainWarehouses.length === 0 ? (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="body2" color="text.secondary">
              No hay almacenes creados aún
            </Typography>
          </Box>
        ) : (
          <List sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
            {mainWarehouses.map((location, index) => (
              <React.Fragment key={location._id}>
                {index > 0 && <Divider />}
                <ListItem
                  secondaryAction={
                    <IconButton
                      edge="end"
                      color="error"
                      onClick={() => handleDelete(location._id)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  }
                >
                  <ListItemIcon>
                    <LocationTypeIcon type={location.type} />
                  </ListItemIcon>
                  <ListItemText
                    primary={location.name}
                    secondary={
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip
                          label={<LocationTypeLabel type={location.type} />}
                          size="small"
                          variant="outlined"
                          sx={{ height: 20, fontSize: "0.7rem" }}
                        />
                        {location.technicianName && (
                          <Typography variant="caption" color="text.secondary">
                            • {location.technicianName}
                          </Typography>
                        )}
                      </Stack>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Información adicional */}
        <Box sx={{ mt: 3, p: 2, bgcolor: "info.50", borderRadius: 2 }}>
          <Typography variant="caption" color="text.secondary">
            <strong>Nota:</strong> Los almacenes de técnicos y residuos peligrosos se gestionan
            automáticamente. Solo puedes crear almacenes principales adicionales.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
}
